import { useEffect, useRef } from 'react';
import type { MapWeatherType } from '../../types/interfaces';
import styles from './CanvasWeatherEffect.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// Sistema de Iluminação e Clima Variável — Camada de Overlay
// ─────────────────────────────────────────────────────────────────────────────
// Camada independente por cima da grade (mas abaixo dos controles de UI),
// 100% `pointer-events: none` — nunca intercepta cliques do jogador/admin.
// A cor/tom é resolvida via CSS (mix-blend-mode + backdrop-filter, ver
// CanvasWeatherEffect.module.css); o Canvas 2D interno só entra para o efeito
// de partículas de chuva (loop de animação), que CSS puro não reproduz bem.

interface CanvasWeatherEffectProps {
  weatherType: MapWeatherType;
}

interface RainDrop {
  x: number;
  y: number;
  len: number;
  speed: number;
  drift: number;
}

const RAIN_DENSITY = 1 / 9000; // gotas por px² de área visível

export function CanvasWeatherEffect({ weatherType }: CanvasWeatherEffectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const dropsRef   = useRef<RainDrop[]>([]);
  const rafRef     = useRef(0);

  useEffect(() => {
    if (weatherType !== 'RAIN' && weatherType !== 'STORM') return;
    const heavy = weatherType === 'STORM'; // tempestade: chuva mais densa e rápida
    const wrapperEl = wrapperRef.current;
    const canvas    = canvasRef.current;
    if (!wrapperEl || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function seedDrops(w: number, h: number) {
      const count = Math.max(50, Math.floor(w * h * RAIN_DENSITY * (heavy ? 1.7 : 1)));
      dropsRef.current = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        len: 10 + Math.random() * 16,
        speed: (8 + Math.random() * 7) * (heavy ? 1.35 : 1),
        drift: (0.4 + Math.random() * 0.4) * (heavy ? 2 : 1),
      }));
    }

    function resize() {
      if (!wrapperEl || !canvas) return;
      const w = wrapperEl.clientWidth;
      const h = wrapperEl.clientHeight;
      if (w <= 0 || h <= 0) return;
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width  = w;
        canvas.height = h;
        seedDrops(w, h);
      }
    }
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(wrapperEl);

    function tick() {
      if (!canvas) return;
      const w = canvas.width, h = canvas.height;
      const c = ctx as CanvasRenderingContext2D;
      c.clearRect(0, 0, w, h);
      c.strokeStyle = 'rgba(190,210,235,0.5)';
      c.lineWidth = 1.2;
      c.lineCap = 'round';
      for (const d of dropsRef.current) {
        c.beginPath();
        c.moveTo(d.x, d.y);
        c.lineTo(d.x - d.drift * d.len * 0.4, d.y + d.len);
        c.stroke();
        d.y += d.speed;
        d.x -= d.drift;
        if (d.y > h) {
          d.y = -d.len;
          d.x = Math.random() * w;
        }
        if (d.x < -20) d.x = w + 20;
      }
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [weatherType]);

  if (weatherType === 'SUNNY') return null; // dia limpo padrão — sem overlay algum

  const overlayClass =
    weatherType === 'SUNSET' ? styles.sunset :
    weatherType === 'NIGHT'  ? styles.night  :
    weatherType === 'FOG'    ? styles.fog    :
    weatherType === 'STORM'  ? styles.storm  :
    /* CLOUDY e RAIN compartilham o mesmo tom acinzentado/dessaturado */ styles.cloudy;

  return (
    <div ref={wrapperRef} className={styles.weatherLayer} aria-hidden="true">
      <div className={`${styles.overlay} ${overlayClass}`} />
      {weatherType === 'NIGHT' && (
        <>
          <div className={`${styles.torchGlow} ${styles.torchA}`} />
          <div className={`${styles.torchGlow} ${styles.torchB}`} />
          <div className={`${styles.torchGlow} ${styles.torchC}`} />
        </>
      )}
      {weatherType === 'FOG' && (
        <>
          <div className={`${styles.mist} ${styles.mistA}`} />
          <div className={`${styles.mist} ${styles.mistB}`} />
        </>
      )}
      {(weatherType === 'RAIN' || weatherType === 'STORM') && <canvas ref={canvasRef} className={styles.rainCanvas} />}
      {weatherType === 'STORM' && <div className={styles.flash} />}
    </div>
  );
}
